import { useMemo } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { applyStage, allIndices, type FilterStage, type FilteredResult } from './filterStore';
import { useBlobStore } from './blobStore';
import { pointsInPolygon, type Vertex } from '../lib/pointInPolygon';
import {
  type Dashboard,
  type SerializedDashboard,
  type Widget,
  type WidgetLayout,
  SCHEMA_VERSION,
  serializeDashboard,
  deserializeDashboard,
  newDashboardId,
  newWidgetId,
} from '../lib/dashboards';

const STORAGE_KEY = 'populace-dashboards-v1';
const GRID_COLS = 12;
const DEFAULT_WIDGET_SIZE: Record<string, { w: number; h: number }> = {
  kpi: { w: 2, h: 2 },
  bar: { w: 6, h: 4 },
  histogram: { w: 6, h: 4 },
  markdown: { w: 6, h: 3 },
};
const FALLBACK_WIDGET_SIZE = { w: 4, h: 4 };

function findNextSlot(
  widgets: Widget[],
  size: { w: number; h: number },
  cols: number,
): { x: number; y: number } {
  const intersects = (x: number, y: number) => {
    for (const w of widgets) {
      if (
        x < w.layout.x + w.layout.w &&
        x + size.w > w.layout.x &&
        y < w.layout.y + w.layout.h &&
        y + size.h > w.layout.y
      ) {
        return true;
      }
    }
    return false;
  };

  const maxBottom = widgets.reduce(
    (m, w) => Math.max(m, w.layout.y + w.layout.h),
    0,
  );
  for (let y = 0; y <= maxBottom; y++) {
    for (let x = 0; x + size.w <= cols; x++) {
      if (!intersects(x, y)) return { x, y };
    }
  }
  return { x: 0, y: maxBottom };
}

export type DashboardSeed = {
  stages: FilterStage[];
  baseSelection: Uint32Array | null;
  polygon: Vertex[] | null;
};

export type AddWidgetOptions = {
  layout?: WidgetLayout;
  slots?: Record<string, string | null>;
  title?: string;
};

type DashboardState = {
  dashboards: Dashboard[];
  createDashboard: (name: string, seed: DashboardSeed) => Dashboard;
  renameDashboard: (id: string, name: string) => void;
  deleteDashboard: (id: string) => void;
  addWidget: (
    dashboardId: string,
    chartTypeId: string,
    options?: AddWidgetOptions,
  ) => Widget | null;
  updateWidget: (
    dashboardId: string,
    widgetId: string,
    patch: Partial<Omit<Widget, 'id'>>,
  ) => void;
  removeWidget: (dashboardId: string, widgetId: string) => void;
  updateLayouts: (
    dashboardId: string,
    layouts: Record<string, WidgetLayout>,
  ) => void;
  moveWidget: (
    dashboardId: string,
    widgetId: string,
    direction: 'up' | 'down',
  ) => void;
  updateFilterStages: (dashboardId: string, stages: FilterStage[]) => void;
};

type PersistedState = {
  dashboards: SerializedDashboard[];
};

export const useDashboardStore = create<DashboardState>()(
  persist(
    (set) => ({
      dashboards: [],

      createDashboard: (name, seed) => {
        const dashboard: Dashboard = {
          id: newDashboardId(),
          name,
          createdAt: new Date().toISOString(),
          filterStages: seed.stages,
          baseSelection: seed.baseSelection,
          polygon: seed.polygon,
          widgets: [],
        };
        set((s) => ({ dashboards: [...s.dashboards, dashboard] }));
        return dashboard;
      },

      renameDashboard: (id, name) =>
        set((s) => ({
          dashboards: s.dashboards.map((d) =>
            d.id === id ? { ...d, name } : d,
          ),
        })),

      deleteDashboard: (id) =>
        set((s) => ({
          dashboards: s.dashboards.filter((d) => d.id !== id),
        })),

      addWidget: (dashboardId, chartTypeId, options) => {
        let added: Widget | null = null;
        set((s) => ({
          dashboards: s.dashboards.map((d) => {
            if (d.id !== dashboardId) return d;
            const size =
              DEFAULT_WIDGET_SIZE[chartTypeId] ?? FALLBACK_WIDGET_SIZE;
            const layout =
              options?.layout ?? {
                ...findNextSlot(d.widgets, size, GRID_COLS),
                w: size.w,
                h: size.h,
              };
            const widget: Widget = {
              id: newWidgetId(),
              chartTypeId,
              slots: options?.slots ?? {},
              layout,
              title: options?.title,
            };
            added = widget;
            return { ...d, widgets: [...d.widgets, widget] };
          }),
        }));
        return added;
      },

      updateWidget: (dashboardId, widgetId, patch) =>
        set((s) => ({
          dashboards: s.dashboards.map((d) =>
            d.id !== dashboardId
              ? d
              : {
                  ...d,
                  widgets: d.widgets.map((w) =>
                    w.id === widgetId ? { ...w, ...patch } : w,
                  ),
                },
          ),
        })),

      removeWidget: (dashboardId, widgetId) =>
        set((s) => ({
          dashboards: s.dashboards.map((d) =>
            d.id !== dashboardId
              ? d
              : { ...d, widgets: d.widgets.filter((w) => w.id !== widgetId) },
          ),
        })),

      updateLayouts: (dashboardId, layouts) =>
        set((s) => ({
          dashboards: s.dashboards.map((d) => {
            if (d.id !== dashboardId) return d;
            return {
              ...d,
              widgets: d.widgets.map((w) => {
                const next = layouts[w.id];
                return next ? { ...w, layout: next } : w;
              }),
            };
          }),
        })),

      moveWidget: (dashboardId, widgetId, direction) =>
        set((s) => ({
          dashboards: s.dashboards.map((d) => {
            if (d.id !== dashboardId) return d;
            const sorted = [...d.widgets].sort((a, b) => {
              if (a.layout.y !== b.layout.y) return a.layout.y - b.layout.y;
              return a.layout.x - b.layout.x;
            });
            const idx = sorted.findIndex((w) => w.id === widgetId);
            const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
            if (idx < 0 || swapIdx < 0 || swapIdx >= sorted.length) return d;
            const a = sorted[idx];
            const b = sorted[swapIdx];
            return {
              ...d,
              widgets: d.widgets.map((w) => {
                if (w.id === a.id) return { ...w, layout: b.layout };
                if (w.id === b.id) return { ...w, layout: a.layout };
                return w;
              }),
            };
          }),
        })),

      updateFilterStages: (dashboardId, stages) =>
        set((s) => ({
          dashboards: s.dashboards.map((d) =>
            d.id !== dashboardId ? d : { ...d, filterStages: stages },
          ),
        })),
    }),
    {
      name: STORAGE_KEY,
      version: SCHEMA_VERSION,
      storage: createJSONStorage(() => localStorage),
      partialize: (state): PersistedState => ({
        dashboards: state.dashboards.map(serializeDashboard),
      }),
      merge: (persisted, current) => {
        const raw = (persisted ?? {}) as Partial<PersistedState>;
        const dashboards = (raw.dashboards ?? []).map(deserializeDashboard);
        return { ...current, dashboards };
      },
      migrate: (persisted, version) => {
        if (version !== SCHEMA_VERSION) {
          console.warn(
            `[dashboardStore] dropping persisted dashboards: schema version ${version} != ${SCHEMA_VERSION}`,
          );
          return { dashboards: [] } satisfies PersistedState;
        }
        return persisted as PersistedState;
      },
    },
  ),
);

export function useDashboardIndices(dashboardId: string): FilteredResult | null {
  const blob = useBlobStore((s) => s.blob);
  const dashboard = useDashboardStore((s) =>
    s.dashboards.find((d) => d.id === dashboardId) ?? null,
  );

  return useMemo(() => {
    if (!blob || !dashboard) return null;
    let start = dashboard.baseSelection;
    if (start === null && dashboard.polygon !== null && dashboard.polygon.length >= 3) {
      // Persisted lasso was over the size cap; reconstruct it from the polygon.
      start = pointsInPolygon(blob.data.positions, dashboard.polygon);
    }
    if (start === null) start = allIndices(blob.data.length);
    const stageCounts: number[] = [];
    let current = start;
    for (const stage of dashboard.filterStages) {
      current = applyStage(stage, blob, current);
      stageCounts.push(current.length);
    }
    return {
      indices: current,
      baseCount: start.length,
      stageCounts,
    };
  }, [blob, dashboard]);
}
